import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  MenuLink, 
  MenuLinkCreateDto, 
  MenuLinkUpdateDto,
  MenuLinkResponse
} from "../../modules/shared/model/menu-link.interfaces";
import { 
  getMenuLinks as getMenuLinksAction,
  getMenuLinkById as getMenuLinkByIdAction,
  createMenuLinkAction,
  updateMenuLinkAction,
  deleteMenuLinkAction,
  bulkCreateMenuLinksAction,
  bulkUpdateMenuLinksAction
} from "../Actions/MenuLinkActions";

// Async Thunks
export const fetchMenuLinks = createAsyncThunk("menuLinks/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getMenuLinksAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch menu links"); 
  }
});

export const fetchMenuLinkById = createAsyncThunk("menuLinks/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getMenuLinkByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch menu link"); 
  }
});

export const createMenuLink = createAsyncThunk("menuLinks/create", async (data: MenuLinkCreateDto, { rejectWithValue }) => {
  try { 
    return await createMenuLinkAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create menu link"); 
  }
});

export const updateMenuLink = createAsyncThunk("menuLinks/update", async ({ id, data }: { id: string; data: MenuLinkUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateMenuLinkAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update menu link"); 
  }
});

export const deleteMenuLink = createAsyncThunk("menuLinks/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteMenuLinkAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete menu link"); 
  }
});

export const bulkCreateMenuLinks = createAsyncThunk("menuLinks/bulkCreate", async (menuLinks: MenuLinkCreateDto[], { rejectWithValue }) => {
  try { 
    return await bulkCreateMenuLinksAction(menuLinks); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk create menu links"); 
  }
});

export const bulkUpdateMenuLinks = createAsyncThunk("menuLinks/bulkUpdate", async (menuLinks: Array<{ id: string; data: MenuLinkUpdateDto }>, { rejectWithValue }) => {
  try { 
    return await bulkUpdateMenuLinksAction(menuLinks); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk update menu links"); 
  }
});

// State interface
interface MenuLinkState {
  // All menu links (paginated)
  menuLinks: MenuLink[];
  currentMenuLink: MenuLink | null;
  menuLinksLoading: boolean;
  menuLinksError: string | null;
  menuLinksTotalCount?: number;
  menuLinksCurrentPage: number;
  menuLinksHasNextPage: boolean;
  menuLinksHasPreviousPage: boolean;
  menuLinksSearchQuery: string;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: MenuLinkState = {
  // All menu links
  menuLinks: [],
  currentMenuLink: null,
  menuLinksLoading: false,
  menuLinksError: null,
  menuLinksTotalCount: 0,
  menuLinksCurrentPage: 1,
  menuLinksHasNextPage: false,
  menuLinksHasPreviousPage: false,
  menuLinksSearchQuery: "",
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const menuLinkSlice = createSlice({
  name: "menuLinks",
  initialState,
  reducers: {
    clearMenuLinksError(state) { 
      state.menuLinksError = null; 
    },
    clearCurrentMenuLink(state) { 
      state.currentMenuLink = null; 
    },
    setMenuLinksSearchQuery(state, action: PayloadAction<string>) { 
      state.menuLinksSearchQuery = action.payload; 
    },
    setMenuLinkFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllMenuLinks(state) {
      state.menuLinks = [];
      state.currentMenuLink = null;
      state.menuLinksTotalCount = 0;
    },
    setMenuLinksCurrentPage(state, action: PayloadAction<number>) {
      state.menuLinksCurrentPage = action.payload;
    },
    updateMenuLinkFromForm(state, action: PayloadAction<Partial<MenuLink>>) {
      if (state.currentMenuLink) {
        state.currentMenuLink = { ...state.currentMenuLink, ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    // All Menu Links Extra Reducers
    builder
      .addCase(fetchMenuLinks.pending, (state) => { 
        state.menuLinksLoading = true; 
        state.menuLinksError = null; 
      })
      .addCase(fetchMenuLinks.fulfilled, (state, action: PayloadAction<MenuLinkResponse>) => { 
        state.menuLinksLoading = false; 
        state.menuLinks = action.payload.data || []; 
        state.menuLinksTotalCount = action.payload.totalCount || 0; 
        state.menuLinksCurrentPage = action.payload.page || 1;
        state.menuLinksHasNextPage = action.payload.hasNextPage || false;
        state.menuLinksHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchMenuLinks.rejected, (state, action: any) => { 
        state.menuLinksLoading = false; 
        state.menuLinksError = action.payload as string; 
      });

    builder
      .addCase(fetchMenuLinkById.pending, (state) => { 
        state.menuLinksLoading = true; 
        state.menuLinksError = null; 
      })
      .addCase(fetchMenuLinkById.fulfilled, (state, action: PayloadAction<MenuLink>) => { 
        state.menuLinksLoading = false; 
        state.currentMenuLink = action.payload; 
      })
      .addCase(fetchMenuLinkById.rejected, (state, action: any) => { 
        state.menuLinksLoading = false; 
        state.menuLinksError = action.payload as string; 
      });

    // Create Extra Reducers
    builder
      .addCase(createMenuLink.pending, (state) => { 
        state.menuLinksLoading = true; 
        state.menuLinksError = null; 
      })
      .addCase(createMenuLink.fulfilled, (state, action: PayloadAction<MenuLink>) => { 
        state.menuLinksLoading = false; 
        state.menuLinks.unshift(action.payload); 
        state.menuLinksTotalCount = (state.menuLinksTotalCount || 0) + 1; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createMenuLink.rejected, (state, action: any) => { 
        state.menuLinksLoading = false; 
        state.menuLinksError = action.payload as string; 
      });

    // Update Extra Reducers
    builder
      .addCase(updateMenuLink.pending, (state) => { 
        state.menuLinksLoading = true; 
        state.menuLinksError = null; 
      })
      .addCase(updateMenuLink.fulfilled, (state, action: PayloadAction<MenuLink>) => {
        state.menuLinksLoading = false;
        const index = state.menuLinks.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.menuLinks[index] = action.payload;
        if (state.currentMenuLink?.id === action.payload.id) state.currentMenuLink = action.payload;
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateMenuLink.rejected, (state, action: any) => { 
        state.menuLinksLoading = false; 
        state.menuLinksError = action.payload as string; 
      });

    // Delete Extra Reducers
    builder
      .addCase(deleteMenuLink.pending, (state) => { 
        state.menuLinksLoading = true; 
        state.menuLinksError = null; 
      })
      .addCase(deleteMenuLink.fulfilled, (state, action: PayloadAction<string>) => { 
        state.menuLinksLoading = false; 
        state.menuLinks = state.menuLinks.filter(x => x.id !== action.payload); 
        if (state.currentMenuLink?.id === action.payload) state.currentMenuLink = null; 
        state.menuLinksTotalCount = Math.max(0, (state.menuLinksTotalCount || 0) - 1); 
      })
      .addCase(deleteMenuLink.rejected, (state, action: any) => { 
        state.menuLinksLoading = false; 
        state.menuLinksError = action.payload as string; 
      });

    // Bulk Create Extra Reducers
    builder
      .addCase(bulkCreateMenuLinks.pending, (state) => { 
        state.menuLinksLoading = true; 
        state.menuLinksError = null; 
      })
      .addCase(bulkCreateMenuLinks.fulfilled, (state, action: PayloadAction<MenuLink[]>) => { 
        state.menuLinksLoading = false; 
        state.menuLinks.unshift(...action.payload); 
        state.menuLinksTotalCount = (state.menuLinksTotalCount || 0) + action.payload.length; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkCreateMenuLinks.rejected, (state, action: any) => { 
        state.menuLinksLoading = false; 
        state.menuLinksError = action.payload as string; 
      });

    // Bulk Update Extra Reducers
    builder
      .addCase(bulkUpdateMenuLinks.pending, (state) => { 
        state.menuLinksLoading = true; 
        state.menuLinksError = null; 
      })
      .addCase(bulkUpdateMenuLinks.fulfilled, (state, action: PayloadAction<MenuLink[]>) => {
        state.menuLinksLoading = false;
        action.payload.forEach(updatedMenuLink => {
          const index = state.menuLinks.findIndex(x => x.id === updatedMenuLink.id);
          if (index !== -1) state.menuLinks[index] = updatedMenuLink;
          if (state.currentMenuLink?.id === updatedMenuLink.id) state.currentMenuLink = updatedMenuLink;
        });
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkUpdateMenuLinks.rejected, (state, action: any) => { 
        state.menuLinksLoading = false; 
        state.menuLinksError = action.payload as string; 
      });
  }
});

export const { 
  clearMenuLinksError,
  clearCurrentMenuLink, 
  setMenuLinksSearchQuery,
  setMenuLinkFormDirty,
  clearAllMenuLinks,
  setMenuLinksCurrentPage,
  updateMenuLinkFromForm
} = menuLinkSlice.actions;

export default menuLinkSlice.reducer;
